/* eslint-disable @typescript-eslint/no-unused-vars */
import { Fab, IconButton } from '@material-ui/core';
import { mapCurrencyCodeToSymbol } from 'app/core/api/product-assignment/api-product-assignment.types';
import {
  useRepairerCase,
  useWarrantyClaim,
} from 'app/core/api/repairer/api-repairer';
import {
  ChatEventType,
  EventEntity,
  WarrantyClaimEvent,
  CaseStatus,
} from 'app/core/api/repairer/api-repairer.types';
import { getOtherUsersName } from 'app/core/api/user/user-utils';
import { UserRole } from 'app/core/api/user/api-user.types';
import { appEnv, AppVariant } from 'app/core/environment';
import {
  logSentry,
  useHandleFetchError,
} from 'app/core/errors/error-utils';
import Button from 'app/shared/components/button/Button';
import TextInput from 'app/shared/components/form-input/TextInput';
import styledFuncs from 'app/styles/styledFuncs';
import styledMixins from 'app/styles/styledMixins';
import {
  useObservableSend,
  usePollInterval,
  usePreviousValue,
  useUrlParams,
  useWatchAsRef,
} from 'app/tools/hooks/misc-hooks';
import useSafeAreaInsets from 'app/tools/hooks/use-safe-area-insets';
import React, {
  KeyboardEventHandler,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { SRLWrapper, SRLWrapperOptions } from 'simple-react-lightbox';
import styled from 'styled-components/macro';
import AddImageAndPreview from 'app/shared/components/form-input/AddImageAndPreview';
import Backdrop from '@material-ui/core/Backdrop';
import {
  makeStyles,
  createStyles,
  Theme,
} from '@material-ui/core/styles';
import MsgItem from '../../components/chat/MsgItem';
import Icon from '../../components/icon/Icon';
import IconLib, {
  ChatMenuSvg,
  NotificationSvg,
} from '../../components/icon/icon-lib';
import { useSetPageTitle } from '../../context-hooks/use-titlebar.hook';
import LoadingOverlay from '../../page-parts/LoadingOverlay';
import { useInitiateMsg } from './chat-utils';
import QuoteDrawer from './quote-drawer';
import CreateQuote from './create-quote';
import WarrantyDrawer from './warranty-drawer';

const ChatPageContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  max-width: 100%;
  background-color: ${p => p.theme.palette.background.default};
`;

const MsgsScrollContainer = styled.div`
  position: relative;
  flex: 1 0 auto;
  overflow-y: auto;
  margin-bottom: 65px;
`;
const MsgsScrollInner = styled.div`
  // NOTE: this prevents the chats from being counted as part of the
  // flow, thus stopping them expanding the outer containers height.
  // Now the immediate container will grow to fill the rest of the
  // page (flex-grow: 1) and not expand any bigger (which would cause
  // unwanted scrolling on a more senior parent).
  position: absolute;
  width: 100%;
  padding: ${styledFuncs.pad(0.5)};
`;
const LastMsgMarker = styled.div`
  // position: relative;
  // bottom: ${styledFuncs.pad(4)};
  margin-bottom: ${styledFuncs.pad(-0.5)};
`;

const InputBoxRow = styled.div`
  position: relative;
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  background-color: white;
  z-index: 2;
  padding: ${styledFuncs.pad()};
  > div {
    flex: 1;
    margin: 0 ${styledFuncs.pad(0.5)};
  }
`;
const ChatInputContainer = styled.div`
  display: flex;
  flex: 0 0 auto;
  width: 100%;
  flex-direction: column;
  position: absolute;
  bottom: 0;
  background: white;
  ${styledMixins.softShadowTiny}
`;

const MsgInput = styled(TextInput).attrs({ minHeight: 40 })`
  .MuiInputBase-root {
    border-radius: 20px;
    padding: ${styledFuncs.pad()};
    flex-grow: 1;
    textarea.MuiOutlinedInput-input {
      max-height: 200px;
      overflow-y: auto !important;
      &::placeholder {
        color: #ddd;
        opacity: 1;
      }
    }
  }
`;

const NotificationDrawer = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  right: 16px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  z-index: 10;
  background-color: white;
  box-shadow: 0px 3px 10px rgb(0 0 0 / 16%);
  border-radius: 10px;
  text-align: center;
  transition: right 3s ease-in-out;
  div {
    flex: 0 1 auto;
    align-self: auto;
  }
`;
const ChatContextMenu = styled.div`
  position: absolute;
  left: 10px;
  bottom: 70px;
  z-index: 3;
  span > div > p {
    box-shadow: 0px 3px 10px rgba(0, 0, 0, 0.161);
    border-radius: 50%;
    color: black;
    font-size: 14px;
    padding: 5px;
    background-color: #fff;
  }
  span > div {
    margin-right: 5px;
  }
  span > span {
    box-shadow: 0px 3px 10px rgba(0, 0, 0, 0.161);
    border-radius: 4px;
    color: black;
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    color: #31ad31;
    padding: 4px 10px;
    background-color: #fff;
  }
`;
const QuickActionRow = styled.div<{ $hide?: boolean }>`
  display: ${p => (p.$hide ? 'none' : 'flex')};
  padding: ${styledFuncs.pad()} 0;
  margin-bottom: ${styledFuncs.pad(-1)};
  overflow-x: auto;
  max-width: 100%;
`;
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    backdrop: {
      zIndex: 2,
      color: '#fff',
    },
  })
);
const SRLOptions: SRLWrapperOptions = {
  caption: { showCaption: false },
  buttons: { showDownloadButton: false }, // enable if CORS support
};

type Props = {
  userRole: UserRole;
  onTabChange?: (val: unknown) => unknown;
  SetIsShowTracking: (isVisible: boolean) => unknown;
};
export default function Chat({
  userRole,
  onTabChange,
  SetIsShowTracking,
}: Props): JSX.Element {
  const history = useHistory();
  const classes = useStyles();
  const isConsumer = appEnv.APP_VARIANT === AppVariant.CONSUMER;
  const isNewChatInitiated = Boolean(history.location.state);
  const [pageRef, setPageRef] = useState<HTMLDivElement | null>(null);
  const [isQuoteDrawerOpen, SetIsQuoteDrawerOpen] =
    useState<boolean>(false);
  const [isLastMessage, SetIsLastMessage] = useState<string>('');
  const [quoteEventEntity, SetQuoteEventEnitity] =
    useState<EventEntity>();
  const [isWarrantyDrawerOpen, SetIsWarrantyDrawerOpen] =
    useState<boolean>(false);
  const [isContextMenuOpen, setIsContextMenuOpen] =
    useState<boolean>(false);
  const [, setIsQuoteDetailOpen] = useState<boolean>(false);
  const [isQuoteCreateOpen, setIsQuoteCreateOpen] =
    useState<boolean>(false);
  const [isNotificationDrawerOpen, setIsNotificationDrawerOpen] =
    useState<boolean>(false);
  const [msg, setMsgState] = useState('');
  const [pickImage, pickImageEvent$] = useObservableSend();
  const [clearImage, clearImageEvent$] = useObservableSend();
  const [pickedImage, setPickedImage] = useState<File | null>(null);

  const msgIsEmpty = !msg.trim() && !pickedImage;

  const { bottom: btm } = useSafeAreaInsets();

  const { id } = useUrlParams(['id']);

  const {
    resource: repairCase,
    request: refreshRepairCase,
    error: fetchError,
    cancel: cancelRefresh,
    loading: loadingCaseUpdate,
    loadingMissing: loadingMissingCase,
  } = useRepairerCase(id);

  const { request$: requestWarranty } = useWarrantyClaim();

  useHandleFetchError(fetchError);

  const initiateMsg = useInitiateMsg(id, cancelRefresh);
  const sendStandardMsg = () => {
    if (msgIsEmpty && !pickedImage) return;
    initiateMsg({ displayText: msg, image: pickedImage })
      .then(() => {
        setMsgState('');
        clearImage();
      })
      .catch(logSentry);
  };

  const loadingUpdateRef = useWatchAsRef(loadingCaseUpdate);
  const pollInterval = usePollInterval();
  useEffect(() => {
    if (
      pollInterval &&
      !isQuoteDrawerOpen &&
      !isQuoteCreateOpen &&
      !isWarrantyDrawerOpen
    ) {
      const interval = setInterval(() => {
        if (!loadingUpdateRef.current) refreshRepairCase();
      }, pollInterval);
      return () => clearInterval(interval);
    }
  }, [
    loadingUpdateRef,
    pollInterval,
    refreshRepairCase,
    isQuoteDrawerOpen,
    isQuoteCreateOpen,
    isWarrantyDrawerOpen,
  ]);

  useEffect(() => {
    if (!isConsumer) {
      if (repairCase?.caseStatus !== 'new') {
        SetIsShowTracking(true);
      }
    }
  }, [SetIsShowTracking, isConsumer, repairCase]);

  const { events: rawEvents = [] } = repairCase || {};

  const [timedChatEvents, setTimedChatEvents] = useState<
    EventEntity[]
  >([]);

  const chatEvents = isNewChatInitiated ? timedChatEvents : rawEvents;

  // NOTE: this effect stagers the initially bot messages to come in
  // at short intervals to simulate real interaction.
  useEffect(() => {
    // if new chat and rawEvents have loaded
    if (isNewChatInitiated && rawEvents?.length) {
      // if not all raw events have been shown, schedule next,
      // otherwise clear new chat status from history state
      if (timedChatEvents.length < rawEvents.length) {
        if (
          rawEvents[timedChatEvents.length].eventType ===
          ChatEventType.CONTEXT_UPDATE
        ) {
          // NOTE: context update comes through immediately
          setTimedChatEvents(s => [...s, rawEvents[s.length]]);
        } else {
          // other events have the timeout applied
          const timeout = setTimeout(
            () =>
              setTimedChatEvents(s => [...s, rawEvents[s.length]]),
            1000
          );
          return () => clearTimeout(timeout);
        }
      } else {
        history.replace({ ...history.location, state: null });
      }
    }
  }, [isNewChatInitiated, rawEvents, history, timedChatEvents]);

  const otherUserName =
    getOtherUsersName(repairCase) ||
    (appEnv.APP_VARIANT === AppVariant.CONSUMER ? 'Case' : 'Job');

  const productName =
    repairCase?.productDefectDetails.assignedProductDetails
      .productDetails.name || 'item';

  useSetPageTitle(`${otherUserName}`);

  const scrollToLast = useCallback(() => {
    pageRef?.scrollIntoView({
      behavior: 'auto',
      block: 'end',
    });
  }, [pageRef]);

  const handleInputKeyPress: KeyboardEventHandler<
    HTMLDivElement
  > = event => {
    if (event.key === 'Enter' && !event.shiftKey) {
      // Stops enter from creating a new line
      event.preventDefault();
      sendStandardMsg();
    }
  };

  const lastEventId = chatEvents?.slice(-1)[0]?.id;

  useEffect(() => {
    // NOTE: will scroll to last if last message id changes (i.e. new message)
    if (!loadingMissingCase && lastEventId) scrollToLast();
  }, [lastEventId, loadingMissingCase, scrollToLast]);

  const { quickActions = [] } = repairCase || {};

  const actionsShape = JSON.stringify(quickActions);
  const prevActionsShape = usePreviousValue(actionsShape, '');

  // NOTE: this is setup so we can clear available actions immediately once one
  // is used, preventing duplicated sending of quick actions.
  const [availableActions, setAvailableActions] =
    useState(quickActions);

  // NOTE: only refresh availableActions if backend value has changed (indicates
  // action has been taken, since the actions change every time one is used atm)
  useEffect(() => {
    if (actionsShape !== prevActionsShape) {
      setAvailableActions(quickActions);
    }
  }, [actionsShape, prevActionsShape, quickActions]);

  const { bottom } = useSafeAreaInsets();
  const purchasePriceCurrencySymbol = mapCurrencyCodeToSymbol.get(
    repairCase?.productDefectDetails.assignedProductDetails
      ?.purchasePrice?.currency
  );
  const NavActionAdd: React.FC = ({ children }) => (
    <div className="flex relative z-10 flex-col-reverse mr-0.5">
      {children}
    </div>
  );
  const OpenQuote = (evt: EventEntity) => {
    // if (
    //   repairCase?.paymentStatus === 'SUCCESS' ||
    //   repairCase?.paymentStatus === 'HOLD'
    // ) {
    //   if (onTabChange) {
    //     onTabChange(2);
    //   }
    // } else if (appEnv.APP_VARIANT === AppVariant.BUSINESS) {
    //   if (onTabChange) {
    //     onTabChange(2);
    //   }
    // } else if (evt === ChatEventType.WARRANTY_CLAIM) {
    //   SetIsWarrantyDrawerOpen(true);
    // } else {
    //   SetIsQuoteDrawerOpen(true);
    // }
    SetQuoteEventEnitity(evt);
    SetIsQuoteDrawerOpen(true);
    const ischatevent = repairCase?.caseStatus;
    SetIsLastMessage(ischatevent ?? '');
  };
  const createWarrantyQuote = () => {
    const body: WarrantyClaimEvent = {
      repaircaseId: Number(id),
    };
    requestWarranty(body).subscribe();
  };
  return (
    <ChatPageContainer
      css={`
        padding-bottom: ${Math.max(bottom - 6, 0)}px;
      `}
    >
      {!isNotificationDrawerOpen &&
        repairCase?.quotationAvailability &&
        appEnv.APP_VARIANT === AppVariant.CONSUMER && (
          <Fab
            style={{
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 10,
              display: 'none',
            }}
            color="primary"
            aria-label="add"
            onClick={() => setIsNotificationDrawerOpen(true)}
          >
            <NotificationSvg />
          </Fab>
        )}
      <NotificationDrawer
        style={{
          display: !isNotificationDrawerOpen ? 'none' : 'flex',
          opacity: !isNotificationDrawerOpen ? '0' : '1',
        }}
      >
        <div>
          <span style={{ lineHeight: '57px', marginLeft: '15px' }}>
            New repair quote added
          </span>
        </div>
        <div>
          <Button onClick={() => OpenQuote({} as EventEntity)}>
            View quote
          </Button>
        </div>
      </NotificationDrawer>
      <QuoteDrawer
        quoteEventEntity={quoteEventEntity}
        isQuoteDrawerOpen={isQuoteDrawerOpen}
        SetIsQuoteDrawerOpen={SetIsQuoteDrawerOpen}
        setIsQuoteDetailOpen={setIsQuoteDetailOpen}
        repairCase={repairCase}
        SetIsShowTracking={SetIsShowTracking}
        isLastMessage={isLastMessage}
      />
      <WarrantyDrawer
        isWarrantyDrawerOpen={isWarrantyDrawerOpen}
        SetIsWarrantyDrawerOpen={SetIsWarrantyDrawerOpen}
        setIsQuoteDetailOpen={setIsQuoteDetailOpen}
        repairCase={repairCase}
      />
      <CreateQuote
        isQuoteCreateOpen={isQuoteCreateOpen}
        setIsQuoteCreateOpen={setIsQuoteCreateOpen}
        repairCase={repairCase}
        refreshRepairCase={refreshRepairCase}
      />
      <MsgsScrollContainer>
        <MsgsScrollInner>
          <SRLWrapper options={SRLOptions}>
            {chatEvents?.map((chatEvent: EventEntity) => (
              <MsgItem
                msg={chatEvent}
                key={chatEvent.id}
                css={(chatEvent.id || 0) < 0 && 'opacity: 0.4;'}
                currencySymbol={purchasePriceCurrencySymbol || '£'}
                {...{ userRole }}
                initiateMsg={initiateMsg}
                OpenQuote={OpenQuote}
              />
            ))}
            <LastMsgMarker ref={setPageRef} />
          </SRLWrapper>
        </MsgsScrollInner>
      </MsgsScrollContainer>
      <LoadingOverlay
        loadingComplete={!loadingMissingCase}
        noPortal
        defaultBgColor
      />
      <ChatInputContainer
        className="bg-white"
        style={{ paddingBottom: `${Math.max(0, btm * 0.6)}px` }}
      >
        {isContextMenuOpen && (
          <>
            <Backdrop
              className={classes.backdrop}
              open={isContextMenuOpen}
              onClick={() => {
                setIsContextMenuOpen(!isContextMenuOpen);
              }}
            />
            <ChatContextMenu>
              {/* <IconButton onClick={() => createWarrantyQuote()}>
              <Icon icon={IconLib.EDIT_NOTE} />{' '}
              <span>Warranty Claim</span>
            </IconButton>
            <br /> */}
              <IconButton
                onClick={() => {
                  setIsQuoteCreateOpen(true);
                  setIsContextMenuOpen(!isContextMenuOpen);
                }}
              >
                <Icon icon={IconLib.EDIT_NOTE} />{' '}
                <span>Issue Quote</span>
              </IconButton>
              <br />
              <IconButton
                onClick={() => {
                  pickImage();
                  setIsContextMenuOpen(!isContextMenuOpen);
                }}
              >
                <Icon icon={IconLib.CAMERA} /> <span>Camera</span>
              </IconButton>
            </ChatContextMenu>
          </>
        )}
        <QuickActionRow $hide={!pickedImage}>
          <AddImageAndPreview
            promptForImage$={pickImageEvent$}
            clearStoredImage$={clearImageEvent$}
            onFileSelected={setPickedImage}
            className="overflow-hidden m-1 ml-2 h-15 rounded-lg"
          />
        </QuickActionRow>
        <InputBoxRow>
          {appEnv.APP_VARIANT === AppVariant.CONSUMER && (
            <IconButton
              onClick={() => pickImage()}
              className="mr-0.5"
              size="small"
              disabled={
                repairCase?.caseStatus === CaseStatus.COMPLETE
              }
            >
              <Icon icon={IconLib.CAMERA} size={36} />
            </IconButton>
          )}
          {appEnv.APP_VARIANT === AppVariant.BUSINESS && (
            <IconButton
              onClick={() => setIsContextMenuOpen(!isContextMenuOpen)}
              className="mr-1 ml-1"
              size="small"
              disabled={
                repairCase?.caseStatus === CaseStatus.COMPLETE
              }
              style={{
                marginTop: '-10px',
                opacity:
                  repairCase?.caseStatus === CaseStatus.COMPLETE
                    ? '0.4'
                    : '1',
              }}
            >
              <ChatMenuSvg />
            </IconButton>
          )}
          <div
            className="flex flex-col flex-grow"
            style={{ flex: 'auto' }}
          >
            <MsgInput
              placeholder="Message"
              value={msg}
              onChange={e => setMsgState(e.target.value)}
              disabled={
                repairCase?.caseStatus === CaseStatus.COMPLETE
              }
              onKeyPress={handleInputKeyPress}
              multiline
            />
          </div>
          <IconButton
            onClick={sendStandardMsg}
            disabled={msgIsEmpty}
            className="ml-0.5"
            size="small"
          >
            <Icon
              icon={IconLib.SEND}
              size={34}
              className="mt-0.2 mr-0.2 -mb-0.1 -ml-0.1"
            />
          </IconButton>
        </InputBoxRow>
      </ChatInputContainer>
    </ChatPageContainer>
  );
}
